/**
 * W8-FINAL-FARE-AND-CASH (D438; findings A-03 and A-04) — the pure view-model behind the ride page's MONEY block, its
 * ROUTE snapshot, the "registrar recebimento" action and the "abrir chamado" action.
 *
 * What the support ride page shows (A-04), from `GET /v1/ops/ride-support/rides/:rideId`:
 *  - the quoted fare and, when the ride completed under a governed `fare.finalRevision`, the FINAL fare with its
 *    direction, reason code and policy version (never a recomputation — the server decided it);
 *  - the cash outcome the driver recorded (received / due / outcome with its reason / commission / booked), or that none
 *    was recorded yet, or that the cash record could not be read (`unavailable` — never a fabricated "unpaid");
 *  - the route the quote priced (provider, reason, distance and duration, degraded flag, cascade version).
 *
 * What it offers (A-03): recording the cash a driver handed in for a COMPLETED ride through `POST /v1/payments/cash`
 * (`payment:settle`), prefilled with what the ride charged (the final fare, else the quote) and never above it. The
 * console only FILTERS and EXPLAINS: the server re-checks the ride, the driver, the currency and the due, and every
 * refusal is explained from the central catalog (`refusal.admin.ride_cash_settle`).
 *
 * Neutral: currencies, markets and reason codes are data; money stays integer minor units end to end.
 */
import {
  CASH_SHORTFALL_REASON_CODES,
  type ApiError,
  type CashSettlementResult,
  type FinalFareView,
  type RideSupportApi,
  type RideSupportDetail,
  type RideSupportRoute,
  type WireMoney,
} from '@vello/api-client';
import { type Result } from '@vello/core';
import { grantsAllow, type KnownGrants } from './console-access.js';

const MINOR = /^\d+$/;
const CURRENCY = /^[A-Z]{3}$/;

/** A wire amount as a non-negative bigint, or undefined when it is not one (never guessed as zero). */
export function wireMinor(m: WireMoney | undefined): bigint | undefined {
  return m !== undefined && MINOR.test(m.amountMinor) ? BigInt(m.amountMinor) : undefined;
}

// ---- the final fare ------------------------------------------------------------------------------

/** The server's final-fare reason vocabulary (`@vello/fare-quote` `FINAL_FARE_REASON_CODES`), mirrored for labels. */
export const FINAL_FARE_REASON_CODES = [
  'FINAL_FARE_AS_QUOTED',
  'FINAL_FARE_REVISED_UP',
  'FINAL_FARE_REVISED_UP_CAPPED',
  'FINAL_FARE_REVISED_DOWN',
  'FINAL_FARE_REVISED_DOWN_FLOORED',
  'FINAL_FARE_DIRECTION_DISABLED',
  'FINAL_FARE_POLICY_UNSET',
  'FINAL_FARE_POLICY_INVALID',
  'FINAL_FARE_NO_QUOTED_ROUTE',
  'FINAL_FARE_NO_BOARDING',
  'FINAL_FARE_PRICING_UNAVAILABLE',
  'FINAL_FARE_ACCOUNT_BILLED_AT_QUOTE',
  'FINAL_FARE_NEGOTIATED',
] as const;

/** The catalog id of a final-fare reason's label, or undefined for a code the console does not know (render it raw). */
export function finalFareReasonMessageId(code: string): string | undefined {
  return (FINAL_FARE_REASON_CODES as readonly string[]).includes(code) ? `rideMoney.finalReason.${code}` : undefined;
}

export type MoneyTone = 'neutral' | 'success' | 'warning' | 'danger';

/** An upward revision is a warning (the rider paid more than quoted); downward is neutral; none is quiet. */
export function finalFareTone(f: Pick<FinalFareView, 'revised' | 'direction'>): MoneyTone {
  if (!f.revised) return 'neutral';
  return f.direction === 'UP' ? 'warning' : 'neutral';
}

/** What the ride charges: the final fare when one was decided, else the quote (the money block's own quote first). */
export function rideDue(detail: Pick<RideSupportDetail, 'money' | 'fare'>): WireMoney {
  // D465b: a negotiated ride's fixed price stands in for the quote when no final fare was decided.
  return detail.money?.finalFare?.final ?? detail.money?.agreedPrice?.total ?? detail.money?.quoted ?? detail.fare.total;
}

// ---- the cash state ------------------------------------------------------------------------------

export type RideCashState =
  /** An older server (no money block) or a failed cash read: say so, never "not paid". */
  | { readonly kind: 'unavailable' }
  | { readonly kind: 'not_recorded' }
  | {
      readonly kind: 'recorded';
      readonly outcome: 'PAID_IN_FULL' | 'PAID_PART' | 'NOT_PAID';
      readonly received: WireMoney;
      readonly due?: WireMoney;
      /** D854: the part of the fare the rider's Saldo Vello credit paid, and what the platform pays the driver for it. */
      readonly creditApplied?: WireMoney;
      readonly platformPaid?: WireMoney;
      readonly commission: WireMoney;
      readonly reasonCode?: string;
      readonly confirmedAt: string;
      readonly booked: boolean;
    };

export function rideCashState(detail: Pick<RideSupportDetail, 'money' | 'unavailable'>): RideCashState {
  if (detail.money === undefined || detail.unavailable.includes('cash')) return { kind: 'unavailable' };
  const cash = detail.money.cash;
  if (!cash.recorded) return { kind: 'not_recorded' };
  return {
    kind: 'recorded',
    outcome: cash.outcome,
    received: cash.received,
    ...(cash.due !== undefined ? { due: cash.due } : {}),
    ...(cash.creditApplied !== undefined ? { creditApplied: cash.creditApplied } : {}),
    ...(cash.platformPaid !== undefined ? { platformPaid: cash.platformPaid } : {}),
    commission: cash.commission,
    ...(cash.reasonCode !== undefined ? { reasonCode: cash.reasonCode } : {}),
    confirmedAt: cash.confirmedAt,
    booked: cash.booked,
  };
}

export function cashOutcomeTone(outcome: 'PAID_IN_FULL' | 'PAID_PART' | 'NOT_PAID'): MoneyTone {
  return outcome === 'PAID_IN_FULL' ? 'success' : outcome === 'PAID_PART' ? 'warning' : 'danger';
}

/** The catalog id of a shortfall reason's label, or undefined for a code the console does not know. */
export function cashShortfallReasonMessageId(code: string): string | undefined {
  return (CASH_SHORTFALL_REASON_CODES as readonly string[]).includes(code) ? `rideMoney.shortfall.${code}` : undefined;
}

// ---- the route snapshot --------------------------------------------------------------------------

/** The D436 route reason vocabulary the console labels (anything else renders as the stable code). */
export const ROUTE_REASON_CODES = [
  'ROUTE_FROM_PROVIDER',
  'ROUTE_FROM_FALLBACK_PROVIDER',
  'ROUTE_SAME_POINT',
  'ROUTE_IMPLAUSIBLE',
  'ROUTE_STRAIGHT_LINE_NO_PROVIDER',
  'ROUTE_STRAIGHT_LINE_POLICY_UNSET',
  'ROUTE_STRAIGHT_LINE_PROVIDERS_FAILED',
  'ROUTE_STRAIGHT_LINE_IMPLAUSIBLE_PROVIDER',
] as const;

export function routeReasonMessageId(code: string): string | undefined {
  return (ROUTE_REASON_CODES as readonly string[]).includes(code) ? `rideMoney.routeReason.${code}` : undefined;
}

/** A degraded route (straight line / fallback) is a warning; a provider route is quiet. */
export function routeTone(route: Pick<RideSupportRoute, 'degraded'>): MoneyTone {
  return route.degraded ? 'warning' : 'neutral';
}

/** Whether a number the route carries can be rendered (a finite, non-negative value). */
export const renderableQuantity = (n: number | undefined): n is number => typeof n === 'number' && Number.isFinite(n) && n >= 0;

// ---- "registrar recebimento" (A-03) --------------------------------------------------------------

/** Why the console does not offer "registrar recebimento" on this ride (display only — the server decides). */
export type CashSettleBlock =
  | 'NO_PERMISSION'
  | 'NOT_COMPLETED'
  | 'NO_DRIVER'
  | 'NOT_CASH'
  | 'CASH_UNKNOWN'
  | 'ALREADY_RECORDED'
  | 'DUE_UNKNOWN';

/** Whether a recorded payment method is cash (the server's method strings, any casing). */
export const isCashMethod = (method: string | undefined): boolean => (method ?? '').trim().toLowerCase() === 'cash';

/**
 * Whether the page offers "registrar recebimento", and with what due. Offered only for a COMPLETED ride with an assigned
 * driver whose cash record READ answered "not recorded". A payment method recorded as something other than cash hides it;
 * an UNKNOWN method does not (a cash ride has no charge row, so the server reads its method as unavailable until it is
 * settled — hiding it there would hide exactly the rides this action is for).
 */
export function cashSettleOffer(
  detail: Pick<RideSupportDetail, 'ride' | 'money' | 'fare' | 'payment' | 'unavailable'>,
  maySettle: boolean,
): { readonly offer: true; readonly due: WireMoney; readonly driverId: string } | { readonly offer: false; readonly block: CashSettleBlock } {
  if (!maySettle) return { offer: false, block: 'NO_PERMISSION' };
  if (detail.ride.status !== 'completed') return { offer: false, block: 'NOT_COMPLETED' };
  const driverId = detail.ride.driverId;
  if (driverId === undefined || driverId === '') return { offer: false, block: 'NO_DRIVER' };
  if (detail.payment !== undefined && !isCashMethod(detail.payment.method)) return { offer: false, block: 'NOT_CASH' };
  const cash = rideCashState(detail);
  if (cash.kind === 'unavailable') return { offer: false, block: 'CASH_UNKNOWN' };
  if (cash.kind === 'recorded') return { offer: false, block: 'ALREADY_RECORDED' };
  const due = rideDue(detail);
  if (wireMinor(due) === undefined || !CURRENCY.test(due.currency)) return { offer: false, block: 'DUE_UNKNOWN' };
  return { offer: true, due, driverId };
}

export type CashSettleDraftIssue = 'AMOUNT_REQUIRED' | 'AMOUNT_NOT_POSITIVE' | 'ABOVE_DUE';

/** Validate the amount typed in the settle dialog against the due (the server's `CASH_AMOUNT_ABOVE_DUE` bound). */
export function cashSettleDraft(amountMinor: bigint | undefined, due: WireMoney): { readonly ok: true; readonly amount: WireMoney } | { readonly ok: false; readonly issue: CashSettleDraftIssue } {
  if (amountMinor === undefined) return { ok: false, issue: 'AMOUNT_REQUIRED' };
  if (amountMinor <= 0n) return { ok: false, issue: 'AMOUNT_NOT_POSITIVE' };
  const dueMinor = wireMinor(due);
  if (dueMinor !== undefined && amountMinor > dueMinor) return { ok: false, issue: 'ABOVE_DUE' };
  return { ok: true, amount: { amountMinor: amountMinor.toString(), currency: due.currency } };
}

/** Record the cash handed in for a ride (the operator is the session; the amount in the ride's currency). */
export function settleRideCash(
  runtime: { readonly rideSupport: RideSupportApi },
  input: { readonly rideId: string; readonly driverId: string; readonly amount: WireMoney },
): Promise<Result<CashSettlementResult, ApiError>> {
  return runtime.rideSupport.settleCash({ rideId: input.rideId, driverId: input.driverId, amount: input.amount });
}

// ---- authority -----------------------------------------------------------------------------------

/**
 * What the operator may do on the ride page's money: settle cash (`payment:settle`, PLATFORM — the server's route table)
 * and open a ticket (`support_ticket:manage`, GEO). Unknown grants → the server decides.
 */
export function rideMoneyAuthority(grants: KnownGrants): { readonly maySettle: boolean; readonly mayOpenTicket: boolean } {
  if (grants === undefined) return { maySettle: true, mayOpenTicket: true };
  return {
    maySettle: grantsAllow(grants, { permission: 'payment:settle', target: 'PLATFORM' }),
    mayOpenTicket: grantsAllow(grants, { permission: 'support_ticket:manage', target: 'GEO' }),
  };
}

// ---- "abrir chamado" (A-04) ----------------------------------------------------------------------

/** The category a ticket opened from the ride page starts on: a cash ride not paid in full is a PAYMENT matter. */
export function rideTicketDefaultCategory(cash: RideCashState): 'TRIP' | 'PAYMENT' {
  return cash.kind === 'recorded' && cash.outcome !== 'PAID_IN_FULL' ? 'PAYMENT' : 'TRIP';
}
